import { AGUAppModuleDefinition } from './app-module-definition';

/**
 * What a Dashboard record's `UIConfigDetails` says this app is made of.
 *
 * Composition is DATA, not code (ui-best-practices §7): one driver class
 * ('AGUAppSurface') serves every AGU app, and which modules an app has is a
 * metadata row pushed with `mj sync`. Adding a new app is therefore a new
 * Application + Dashboard row and zero TypeScript.
 *
 * `UIConfigDetails` is free for us to define here because MJ only interprets it
 * for `Type='Config'` dashboards (see ng-explorer-core's
 * single-dashboard.component, which parses it as `{items:[]}`); ours are all
 * `Type='Code'`.
 *
 * Example — the composite app:
 *   {"modules":["dashboard","grants","people","jobs"],"landing":"dashboard"}
 * Example — a single-module app:
 *   {"modules":["grants"]}
 */
export interface AGUAppComposition {
    /** Module keys, in the order they appear in nav. */
    readonly ModuleKeys: readonly string[];
    /** Module to land on. Defaults to the first entry of ModuleKeys. */
    readonly LandingModuleKey: string | null;
}

/** Wire shape of UIConfigDetails, before validation. */
interface AGUAppCompositionWire {
    readonly modules?: unknown;
    readonly landing?: unknown;
}

/** Result of parsing, so callers can render a real error instead of a blank app. */
export type AGUAppCompositionParseResult =
    | { readonly Ok: true; readonly Composition: AGUAppComposition }
    | { readonly Ok: false; readonly Error: string };

/**
 * Parse and validate a Dashboard record's `UIConfigDetails` into a composition.
 *
 * Fails CLOSED and LOUD. An absent, empty, malformed, or empty-`modules` config
 * is an error result, never a silent fallback to "all registered modules" — that
 * fallback would hand every module of every future app to whichever Dashboard
 * row someone forgot to configure. The Feedback Platform surface set the same
 * precedent for unresolved roles.
 */
export function ParseAppComposition(
    uiConfigDetails: string | null | undefined
): AGUAppCompositionParseResult {
    const raw = (uiConfigDetails ?? '').trim();
    if (raw.length === 0) {
        return {
            Ok: false,
            Error: 'This app has no module configuration. Set UIConfigDetails on its Dashboard record, e.g. {"modules":["grants"]}.',
        };
    }

    let wire: AGUAppCompositionWire;
    try {
        const parsed: unknown = JSON.parse(raw);
        if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
            return { Ok: false, Error: 'Module configuration must be a JSON object.' };
        }
        wire = parsed as AGUAppCompositionWire;
    } catch {
        return { Ok: false, Error: 'Module configuration is not valid JSON.' };
    }

    if (!Array.isArray(wire.modules)) {
        return { Ok: false, Error: 'Module configuration is missing a "modules" array.' };
    }

    // De-duplicate while preserving order: a repeated key would render the same
    // nav item twice and make the "is this composite?" test lie.
    const seen = new Set<string>();
    const moduleKeys: string[] = [];
    for (const entry of wire.modules) {
        if (typeof entry !== 'string' || entry.trim().length === 0) {
            return { Ok: false, Error: 'Every entry in "modules" must be a non-empty string.' };
        }
        const key = entry.trim();
        if (!seen.has(key)) {
            seen.add(key);
            moduleKeys.push(key);
        }
    }
    if (moduleKeys.length === 0) {
        return { Ok: false, Error: 'Module configuration lists no modules.' };
    }

    let landing: string | null = null;
    if (wire.landing !== undefined) {
        if (typeof wire.landing !== 'string' || !seen.has(wire.landing.trim())) {
            return {
                Ok: false,
                Error: `"landing" must name one of this app's own modules (${moduleKeys.join(', ')}).`,
            };
        }
        landing = wire.landing.trim();
    }

    return { Ok: true, Composition: { ModuleKeys: moduleKeys, LandingModuleKey: landing } };
}

/**
 * Resolve a composition against the module registry and the user's roles.
 *
 * Two independent narrowings, in this order:
 *   1. COMPOSITION — only modules this app names, in the app's declared order.
 *      A key with no registered definition is reported, not skipped silently:
 *      it means a metadata row names a module whose code is not deployed.
 *   2. ENTITLEMENT — only modules the user's roles satisfy. Fail closed: a
 *      module listing roles the user lacks is dropped.
 *
 * Role comparison is case-insensitive because MJ role names are authored by hand
 * in metadata ('admin', 'MAPS Admin') and a casing slip must not silently revoke
 * access.
 */
export function ResolveComposedModules(
    composition: AGUAppComposition,
    registry: readonly AGUAppModuleDefinition[],
    userRoleNames: readonly string[]
): {
    readonly Modules: readonly AGUAppModuleDefinition[];
    readonly MissingKeys: readonly string[];
} {
    const byKey = new Map(registry.map((definition) => [definition.Key, definition]));
    const heldRoles = new Set(userRoleNames.map((name) => name.toLowerCase()));

    const modules: AGUAppModuleDefinition[] = [];
    const missingKeys: string[] = [];
    for (const key of composition.ModuleKeys) {
        const definition = byKey.get(key);
        if (!definition) {
            missingKeys.push(key);
            continue;
        }
        const entitled =
            definition.RequiredRoleNames.length === 0 ||
            definition.RequiredRoleNames.some((role) => heldRoles.has(role.toLowerCase()));
        if (entitled) {
            modules.push(definition);
        }
    }
    return { Modules: modules, MissingKeys: missingKeys };
}

/**
 * Sibling modules of `key` fit for a dashboard's own tile rail — every other
 * module in the composition, excluding `key` itself and any module marked
 * `HiddenFromNav`. Preserves the composition's nav order.
 *
 * `HiddenFromNav` modules (a profile page reached from the sidebar identity
 * block, a search page reached from the sidebar search box) are navigable
 * destinations but not nav-rail entries, and a member dashboard's tile rail
 * is exactly the sidebar's own rail restated as cards — a module the sidebar
 * hides must not resurface as a card here. `AGUAppSidebarComponent
 * .VisibleModules` applies the identical `!m.HiddenFromNav` predicate for the
 * nav rail itself; this is that same filter for a dashboard's siblings.
 */
export function VisibleSiblingsOf(
    modules: readonly AGUAppModuleDefinition[],
    key: string
): readonly AGUAppModuleDefinition[] {
    return modules.filter((module) => module.Key !== key && !module.HiddenFromNav);
}

/**
 * The module a surface should open on: the declared landing when it survived
 * entitlement filtering, otherwise the first module that did. Null when the user
 * is entitled to nothing in this app.
 */
export function PickLandingModule(
    composition: AGUAppComposition,
    modules: readonly AGUAppModuleDefinition[]
): AGUAppModuleDefinition | null {
    if (modules.length === 0) {
        return null;
    }
    const landing = composition.LandingModuleKey;
    if (landing !== null) {
        // `??` not `||`: a legitimately-found module is never falsy, and we want
        // the fallback to fire only on "not found".
        return modules.find((definition) => definition.Key === landing) ?? modules[0];
    }
    return modules[0];
}
